import type { BaseState } from "../base/api.js";

type Layout = Pick<BaseState, "grid" | "buildings" | "zones" | "loop">;
const key = (x: number, y: number) => `${x},${y}`;
const adjacent = (x: number, y: number) => [[x, y + 1], [x + 1, y], [x, y - 1], [x - 1, y]] as const;

/** A connected service network follows the actual layout, including a relocated HQ. No roads cut through buildings. */
export function settlementRoads(state: Layout): Set<string> {
  const walkable = new Set<string>();
  for (const rect of [state.grid.core, ...state.zones.filter(z => z.state === "open").map(z => z.rect)])
    for (let y = rect.y; y < rect.y + rect.h; y++) for (let x = rect.x; x < rect.x + rect.w; x++) walkable.add(key(x, y));
  for (const b of state.buildings)
    for (let y = b.y; y < b.y + b.h; y++) for (let x = b.x; x < b.x + b.w; x++) walkable.delete(key(x, y));
  for (const d of state.loop?.decorations ?? []) walkable.delete(key(d.x, d.y));

  const roads = new Set<string>();
  const core = state.grid.core;
  const gate = [...walkable].map(cell => ({ cell, xy: cell.split(",").map(Number) }))
    .sort((a, b) => (Math.abs(a.xy[0]! - (core.x + core.w / 2)) + Math.abs(a.xy[1]! - (core.y + core.h - 1)) * 2)
      - (Math.abs(b.xy[0]! - (core.x + core.w / 2)) + Math.abs(b.xy[1]! - (core.y + core.h - 1)) * 2))[0]?.cell;
  if (!gate) return roads;
  roads.add(gate);
  const buildings = [...state.buildings].filter(b => b.w > 0).sort((a, b) => Number(b.buildingId === "bld.command_nexus") - Number(a.buildingId === "bld.command_nexus") || a.id.localeCompare(b.id));
  for (const b of buildings) {
    // Prefer the facade centre on its rotated front. Fall back to another accessible edge if the player blocks it.
    const front = ((b.rot % 360) + 360) % 360;
    const centreX = b.x + Math.floor(b.w / 2), centreY = b.y + Math.floor(b.h / 2);
    const preferred = front === 90 ? key(b.x - 1, centreY) : front === 180 ? key(centreX, b.y - 1) : front === 270 ? key(b.x + b.w, centreY) : key(centreX, b.y + b.h);
    const edges = new Set<string>();
    for (let x = b.x; x < b.x + b.w; x++) { edges.add(key(x, b.y - 1)); edges.add(key(x, b.y + b.h)); }
    for (let y = b.y; y < b.y + b.h; y++) { edges.add(key(b.x - 1, y)); edges.add(key(b.x + b.w, y)); }
    const destinations = walkable.has(preferred) ? new Set([preferred]) : new Set([...edges].filter(cell => walkable.has(cell)));
    const queue = [...roads], parents = new Map<string, string | null>(queue.map(cell => [cell, null]));
    let reached: string | undefined, fallback: string | undefined;
    for (let i = 0; i < queue.length; i++) {
      const cell = queue[i]!;
      if (!fallback && edges.has(cell)) fallback = cell;
      if (destinations.has(cell)) { reached = cell; break; }
      const [x, y] = cell.split(",").map(Number) as [number, number];
      for (const [xx, yy] of adjacent(x, y)) {
        const next = key(xx, yy);
        if (!walkable.has(next) || parents.has(next)) continue;
        parents.set(next, cell); queue.push(next);
      }
    }
    reached ??= fallback;
    while (reached) { roads.add(reached); reached = parents.get(reached) ?? undefined; }
  }
  return roads;
}
